import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { Download } from 'lucide-react';
import { toast } from 'react-toastify';

import api from '../../common/apiClient';
import SummaryApi from '../../common/SummaryApi';
import ModernTable from '../ui/ModernTable';
import SelectDropdown from '../../customStyles/SelectDropdown';
import { formatDateTime } from '../../helpers/formatDateTime';

const STATUS_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Acknowledged', value: 'acknowledged' },
  { label: 'Pending', value: 'pending' },
];

const PolicyAcknowledgementReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(SummaryApi.getPolicyAcknowledgementsReport.url(id), {
        params: { status: statusFilter || undefined, page: currentPage, limit: itemsPerPage },
      });
      if (res.data.success) {
        setRows(res.data.data || []);
        setTotal(res.data.total || 0);
      }
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [id, statusFilter, currentPage, itemsPerPage]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExport = async () => {
    try {
      const res = await api.get(SummaryApi.exportPolicyAcknowledgementsReport.url(id), { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `acknowledgement-report-${id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error('Export failed');
    }
  };

  const columns = [
    {
      label: 'Employee',
      key: 'employeeId',
      render: (row) => (
        <div>
          <div className="text-sm font-semibold text-gray-900">{row.employeeId?.firstName} {row.employeeId?.lastName}</div>
          <div className="text-xs text-gray-500">{row.employeeId?.userId}</div>
        </div>
      ),
    },
    {
      label: 'Status',
      key: 'status',
      render: (row) => (
        <span className={`px-2 py-1 text-xs rounded-full font-medium ${row.status === 'acknowledged' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
          {row.status}
        </span>
      ),
    },
    { label: 'Version', key: 'versionNumber', render: (row) => <span className="text-sm">v{row.versionNumber}</span> },
    { label: 'Acknowledged At', key: 'acknowledgedAt', render: (row) => <span className="text-sm">{row.acknowledgedAt ? formatDateTime(row.acknowledgedAt, true) : '-'}</span> },
    { label: 'IP Address', key: 'ipAddress', render: (row) => <span className="text-xs text-gray-500">{row.ipAddress || '-'}</span> },
    { label: 'Reminders Sent', key: 'remindersSent', render: (row) => <span className="text-sm">{row.remindersSent}</span> },
  ];

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="rounded-lg bg-red-50 border border-red-100 p-1.5 sm:p-2">
            <FaArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Acknowledgement Report</h1>
        </div>
        <button onClick={handleExport} className="flex items-center gap-2 bg-white border px-4 py-2 rounded-lg text-sm">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <div className="mb-4 max-w-xs">
        <SelectDropdown label="Status" options={STATUS_OPTIONS} value={statusFilter} onChange={(v) => { setStatusFilter(v); setCurrentPage(1); }} valueKey="value" labelKey="label" />
      </div>

      <ModernTable
        columns={columns}
        data={rows}
        loading={loading}
        pagination={{
          totalItems: total,
          itemsPerPage,
          currentPage,
          onPageChange: setCurrentPage,
          onItemsPerPageChange: (val) => { setItemsPerPage(val); setCurrentPage(1); },
        }}
      />
    </div>
  );
};

export default PolicyAcknowledgementReport;
